import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, TextField, CircularProgress } from '@mui/material';
import { useFormContext, Controller, useForm } from 'react-hook-form';
import InfoBox from '../../../../core/components/ui/InfoBox';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../core/store';
import PhoneInputWithLabel from '../../../../core/components/ui/PhoneInputWithLabel';
import { useAuth } from '../../../auth/hooks/useAuth';
import { useUpdateEnrollmentMutation } from '../../hooks/enrollmentApi';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../../core/components';
import { stepConfig } from '../../utils/enrollmentStepConfig';

interface EmergencyContactStepProps {
  isSaving?: boolean;
}

const EmergencyContactStep: React.FC<EmergencyContactStepProps> = ({ isSaving =false}) => {
  const emergencyContact = useSelector((state: RootState) => state.emergencyContact);
  const { control, formState: { errors }, reset, clearErrors,  } = useFormContext();
  useEffect(() => {
    if (!emergencyContact) return;

    let isInitialRender = true;

    if (isInitialRender) {
      reset(
        {
          contactName: emergencyContact.contactName || '',
          contactPhone: emergencyContact.contactPhone || '',
          contactRelationship: emergencyContact.contactRelationship || '',
        },
        { keepDefaultValues: true }
      );
      isInitialRender = false;
    }
  }, [
    emergencyContact?.contactName,
    emergencyContact?.contactPhone,
    emergencyContact?.contactRelationship,
    reset,
  ]);

  return (
    <Box>
      <Typography variant='h5' color='text.primary' sx={{ mb: { xs: 2, sm: 3 } }}>
        Emergency Contact Information
      </Typography>
      <Typography variant='caption' component={'p'} color='text.secondary' lineHeight={1.5} sx={{ mb: { xs: 2, sm: 3 } }} >Emergency contact information is required for individuals under the age of 18. Please provide the name, relationship, and phone number of a parent or legal guardian.</Typography>

      <Box sx={{ mb: 4 }}>
        <Controller
          name='contactName'
          control={control}
          render={({ field }) => (
            <TextField
              fullWidth
              label='Contact Name'
              {...field}
              value={field.value || ''}
              variant='outlined'
              error={Boolean(errors.contactName)}
              sx={{ mb: 3 }}
              disabled={isSaving}
            />
          )}
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Controller
          name='contactPhone'
          control={control}
          render={({ field }) => (
            <PhoneInputWithLabel
              value={field.value || ''}
              onChange={(value) => {
                field.onChange(value);
                clearErrors("contactPhone")
              }}
              countryCodeEditable={false}
              disableCountryCode={true}
              label='Contact Phone'
              disabled={false}
              error={Boolean(errors.contactPhone)}
              required
            />
          )}
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Controller
          name='contactRelationship'
          control={control}
          render={({ field }) => (
            <TextField
              fullWidth
              label='Relationship to You'
              {...field}
              value={field.value || ''}
              variant='outlined'
              error={Boolean(errors.contactRelationship)}
              sx={{ mb: 3 }}
              disabled={isSaving}
            />
          )}
        />
      </Box>

      <InfoBox>
        Your emergency contact will only be contacted in case of an emergency where concerns arise
        regarding your safety or wellbeing.
      </InfoBox>

      {/* <Button
        variant='contained'
        onClick={handleSubmit}
        disabled={isSaving}
        color={'primary'}
        sx={{
          minWidth: 180,
          position: 'relative',
          gap: 1,
          fontWeight: 'bold',
          order: { xs: 2, sm: 2 },
          flexGrow: { xs: 1, sm: 0 }
        }}
      >
        {isSaving ? (
          <>
            <CircularProgress size={20} color='inherit' sx={{ mr: 1 }} />
            <Typography variant='button' component='span'>
              Saving...
            </Typography>
          </>
        ) : (
          <>

            Submit
          </>
        )}
      </Button> */}
    </Box>
  );
};

export default React.memo(EmergencyContactStep);
